import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

// Plain <textarea> that is uncontrolled in the DOM, so external dictation tools
// (Wispr Flow, OS dictation, extensions) can inject text without React reverting it.
export const InjectableTextarea = forwardRef(({ value, onValueChange, className, readOnly, ...props }, ref) => {
  const el = useRef(null);
  const last = useRef(value ?? "");
  useImperativeHandle(ref, () => el.current);

  const sync = useCallback(() => {
    const v = el.current?.value ?? "";
    if (v !== last.current) {
      last.current = v;
      onValueChange(v);
    }
  }, [onValueChange]);

  // Parent-driven updates (mic dictation, clear) write into the DOM
  useLayoutEffect(() => {
    const v = value ?? "";
    if (el.current && v !== el.current.value) {
      el.current.value = v;
      last.current = v;
    }
  }, [value]);

  // Catch silent injections into an unfocused field on any re-render
  useEffect(() => { sync(); });

  useEffect(() => {
    const node = el.current;
    let timer;
    const deferred = () => setTimeout(sync, 0);
    const startPoll = () => { clearInterval(timer); timer = setInterval(sync, 250); };
    const stopPoll = () => { clearInterval(timer); sync(); };
    const events = ["input", "change", "keyup", "compositionend", "cut", "drop"];
    events.forEach((ev) => node.addEventListener(ev, sync));
    node.addEventListener("paste", deferred);
    node.addEventListener("focus", startPoll);
    node.addEventListener("blur", stopPoll);
    if (document.activeElement === node) startPoll();
    return () => {
      clearInterval(timer);
      events.forEach((ev) => node.removeEventListener(ev, sync));
      node.removeEventListener("paste", deferred);
      node.removeEventListener("focus", startPoll);
      node.removeEventListener("blur", stopPoll);
    };
  }, [sync]);

  return (
    <textarea
      ref={el}
      defaultValue={value ?? ""}
      onChange={sync}
      readOnly={readOnly}
      className={cn(
        "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      {...props}
    />
  );
});
InjectableTextarea.displayName = "InjectableTextarea";
